import { Provider } from '@fruitful/types';
import { useQueryClient } from '@tanstack/react-query';
import { router } from 'expo-router';
import { useAtom, useAtomValue } from 'jotai';
import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import {
    BackButton,
    BackButtonIconType,
    BodyMediumRegular,
    CheckIcon,
    H5Emphasized,
    Icon,
    Image,
    SafeAreaView,
    Spinner,
    tokens,
    View,
    XStack,
    YStack,
} from 'smoothie';

import FruitfulLogoIcon from '../../../../assets/images/logos/fruitful-logo-icon-only-white.svg';
import useGetBillDiscoveryReview from '../../../bills/hooks/useGetBillDiscoveryReview';
import { useGetAllBills } from '../../../bills/hooks/useGetBills';
import {
    AccountImportStatus,
    useGetMembershipAccountImportStatuses,
} from '../../../bills/hooks/useGetMembershipAccountImportStatuses';
import { billReviewSourceAtom } from '../../../bills/state/BillsAtoms';
import { useAuthedUser } from '../../../common/providers/AuthedUserProvider';
import { useViewEvents } from '../../../events/hooks/useViewEvents';
import {
    hasAnyExternalHolisticAccountsAtom,
    holisticAccountsAtom,
} from '../../../holistic/state/HolisticAtoms';
import LogRocketFacade from '../../../logger/log-rocket/LogRocketFacade';
import { useGetLiabilities } from '../../hooks/useGetLiabilities';
import { useOnboardingCompleteness } from '../../hooks/useOnboardingCompleteness';

const SpinwheelBillsProcessing = () => {
    const { trackViewOnce } = useViewEvents();
    trackViewOnce('SpinwheelBillsProcessing');

    const user = useAuthedUser();
    const queryClient = useQueryClient();
    const [billReviewSource] = useAtom(billReviewSourceAtom);
    const { isBillsComplete } = useOnboardingCompleteness();

    const [shouldPollForPendingJobs, setShouldPollForPendingJobs] =
        useState(true);
    const [showSuccessState, setShowSuccessState] = useState(false);

    // Get all holistic accounts and filter by Quiltt
    const [allHolisticAccounts] = useAtom(holisticAccountsAtom);
    const hasAnyExternalHolisticAccounts = useAtomValue(
        hasAnyExternalHolisticAccountsAtom,
    );
    const quilttAccounts = allHolisticAccounts.filter(
        (account) => account.provider === Provider.QUILTT,
    );

    // Group by institution and take first account from each institution
    const holisticAccounts = useMemo(() => {
        const institutionMap = new Map<
            string,
            (typeof quilttAccounts)[number]
        >();
        quilttAccounts.forEach((account) => {
            if (!institutionMap.has(account.institutionName)) {
                institutionMap.set(account.institutionName, account);
            }
        });
        return Array.from(institutionMap.values());
    }, [quilttAccounts]);

    // Rotate through institutions every 10 seconds
    const [currentInstitutionIndex, setCurrentInstitutionIndex] = useState(0);

    useEffect(() => {
        if (holisticAccounts.length <= 1) return;

        const interval = setInterval(() => {
            setCurrentInstitutionIndex(
                (prev) => (prev + 1) % holisticAccounts.length,
            );
        }, 10000);

        return () => {
            clearInterval(interval);
        };
    }, [holisticAccounts.length]);

    const {
        data: liabilitiesData,
        refetch: refetchLiabilities,
        isLoading: isLoadingLiabilities,
    } = useGetLiabilities();

    const {
        allBills: billsData,
        isLoadingAllBills: isLoadingBills,
        refetchBills,
    } = useGetAllBills();

    const { refetch: refetchBillDiscoveryReview } = useGetBillDiscoveryReview(
        user.membership?.id ?? '',
    );

    const { membershipAccountImportStatuses } =
        useGetMembershipAccountImportStatuses(
            user.membership?.id ?? '',
            shouldPollForPendingJobs,
        );

    // Derive billsStillProcessing directly from membershipAccountImportStatuses
    // to avoid race conditions with useState
    const billsStillProcessing = useMemo(() => {
        LogRocketFacade.log(
            `Account import statuses: ${JSON.stringify(membershipAccountImportStatuses)}`,
        );

        const totalAccountsChecked =
            membershipAccountImportStatuses?.totalAccountsChecked;

        const noAccountsChecked = totalAccountsChecked === 0;

        // If it's null/undefined, use backwards compatibility (treat as 1)
        // Note: ?? only treats null/undefined as nullish, NOT 0, so 0 will be preserved correctly
        const numAccountsChecked = totalAccountsChecked ?? 1;

        const accountsThatAreStillProcessing = (
            membershipAccountImportStatuses?.accountStatusesAndCounts ?? []
        ).filter((accountImportStatus) => {
            return (
                [
                    AccountImportStatus.PENDING,
                    AccountImportStatus.UNIMPORTED,
                ].includes(accountImportStatus.status) ||
                (!accountImportStatus.hasRunBillsJobAfterImport &&
                    accountImportStatus.status !== AccountImportStatus.ERROR)
            );
        });

        if (accountsThatAreStillProcessing.length > 0) {
            LogRocketFacade.log(
                `Accounts still processing: ${accountsThatAreStillProcessing.length}`,
            );
            LogRocketFacade.log(
                `Account details: ${JSON.stringify(
                    accountsThatAreStillProcessing.map((a) => ({
                        id: a.accountId,
                        status: a.status,
                        hasRunBillsJob: a.hasRunBillsJobAfterImport,
                        numTransactions: a.numTransactions,
                    })),
                )}`,
            );
        }

        // If noAccountsChecked (0) AND we have external accounts, we haven't started yet - keep waiting
        // If noAccountsChecked (0) AND we have NO external accounts, that's correct - don't wait
        // Otherwise, check if there are accounts still processing or pending bill jobs
        const stillProcessing =
            (noAccountsChecked && hasAnyExternalHolisticAccounts) ||
            (numAccountsChecked > 0 &&
                (accountsThatAreStillProcessing.length !== 0 ||
                    !!membershipAccountImportStatuses?.successfulBillJobData
                        ?.hasPendingBillJob));

        LogRocketFacade.log(
            `Bills still processing: ${stillProcessing} (totalAccountsChecked: ${totalAccountsChecked}, numAccountsChecked: ${numAccountsChecked}, hasAnyExternalHolisticAccounts: ${hasAnyExternalHolisticAccounts}, accountsStillProcessing: ${accountsThatAreStillProcessing.length}, hasPendingBillJob: ${!!membershipAccountImportStatuses?.successfulBillJobData?.hasPendingBillJob})`,
        );

        return stillProcessing;
    }, [membershipAccountImportStatuses, hasAnyExternalHolisticAccounts]);

    const refetchEverything = async () => {
        const queryKeys = [
            `getBills/${user?.membership?.id ?? ''}`,
            `getBills/${user?.membership?.id ?? ''}/ACTIVE`,
            `getBills/${user?.membership?.id ?? ''}/undefined`,
            `useGetBillDiscoveryReview/${user?.membership?.id ?? ''}`,
            'getPendingJobs',
        ];

        // Only invalidate liabilities if not skipping Spinwheel
        if (user?.spinwheelOverride !== true) {
            queryKeys.push('getLiabilities');
        }

        await queryClient.invalidateQueries({
            queryKey: queryKeys,
        });

        await refetchBillDiscoveryReview();
        refetchBills();

        // Only refetch liabilities if not skipping Spinwheel
        if (user?.spinwheelOverride !== true) {
            void refetchLiabilities();
        }
    };

    // Handle side effects when processing status changes
    useEffect(() => {
        if (!billsStillProcessing) {
            LogRocketFacade.log(
                'Processing complete - refetching bills and liabilities',
            );
            void refetchEverything();
        }
        setShouldPollForPendingJobs(billsStillProcessing);
    }, [billsStillProcessing]);

    // Show success state for 2 seconds when processing completes
    useEffect(() => {
        // Don't show success until we have account import status data
        // This prevents showing success on initial mount with cached bills/liabilities data
        const hasAccountStatusData =
            membershipAccountImportStatuses?.accountStatusesAndCounts != null;

        LogRocketFacade.log(
            `Success state check - hasAccountStatusData: ${hasAccountStatusData}, billsStillProcessing: ${billsStillProcessing}, isLoadingBills: ${isLoadingBills}, hasBillsData: ${billsData != null}, isLoadingLiabilities: ${isLoadingLiabilities}, hasLiabilitiesData: ${liabilitiesData != null}`,
        );

        // If spinwheelOverride is true, we're skipping Spinwheel so liabilitiesData may be null
        const shouldRequireLiabilities = !(user?.spinwheelOverride === true);

        if (
            hasAccountStatusData &&
            !billsStillProcessing &&
            !isLoadingBills &&
            billsData != null &&
            (!shouldRequireLiabilities ||
                (!isLoadingLiabilities && liabilitiesData != null))
        ) {
            LogRocketFacade.log('All conditions met - showing success state');
            setShowSuccessState(true);
            const timer = setTimeout(() => {
                LogRocketFacade.log('Navigating to bills review page');
                // Navigate to bills review page (use testing route if in testing context)
                const reviewRoute =
                    billReviewSource === 'spinwheel-testing'
                        ? '/expenses/bills-review'
                        : '/onboarding/expenses/bills-review';

                // Preserve source param when redirecting
                const sourceParam =
                    billReviewSource === 'home-review'
                        ? 'before-money-map-session'
                        : billReviewSource === 'home'
                          ? 'home'
                          : undefined;

                router.replace({
                    pathname: reviewRoute,
                    ...(sourceParam && { params: { source: sourceParam } }),
                });
            }, 2000);
            return () => {
                clearTimeout(timer);
            };
        }
        return undefined;
    }, [
        billsStillProcessing,
        isLoadingBills,
        billsData,
        isLoadingLiabilities,
        liabilitiesData,
        billReviewSource,
        membershipAccountImportStatuses,
    ]);

    const handleGoBack = () => {
        if (billReviewSource === 'profile') {
            router.navigate('/settings');
        } else if (
            billReviewSource === 'home-review' ||
            billReviewSource === 'home'
        ) {
            // Coming from BeforeMoneyMapSession or from home - always go to home
            router.navigate('/home');
        } else {
            router.navigate(
                isBillsComplete ? '/home' : '/onboarding/connect-accounts',
            );
        }
    };

    return (
        <SafeAreaView
            style={{
                flex: 1,
                backgroundColor: tokens.color.light.brand.white,
                maxWidth: 700,
                minWidth: 400,
                alignSelf: 'center',
            }}
        >
            <YStack flex={1}>
                {/* Header */}
                <XStack
                    paddingHorizontal={24}
                    paddingTop={16}
                    paddingBottom={16}
                    alignItems="center"
                    justifyContent="space-between"
                >
                    <BackButton
                        fallbackRoute="/home"
                        customBackFn={handleGoBack}
                        iconType={BackButtonIconType.ARROW_LEFT}
                    />
                    <XStack width={40} />
                </XStack>

                {/* Content */}
                <YStack flex={1} paddingHorizontal={24}>
                    <YStack alignItems="flex-start" gap={16} marginBottom={80}>
                        <H5Emphasized color="$black" textAlign="left">
                            Gathering your recurring bills
                        </H5Emphasized>
                        <BodyMediumRegular color="$gray1000" textAlign="left">
                            Please wait a few minutes.
                        </BodyMediumRegular>
                    </YStack>

                    {/* Rotating Institution Animation */}
                    <YStack gap={12} width="100%">
                        <XStack
                            key={
                                showSuccessState
                                    ? 'success'
                                    : (holisticAccounts[currentInstitutionIndex]
                                          ?.id ?? 'none')
                            }
                            alignItems="center"
                            paddingVertical={16}
                            paddingHorizontal={20}
                            backgroundColor="$gray100"
                            borderRadius={12}
                            animation="lazy"
                            enterStyle={{
                                opacity: 0,
                                y: -20,
                            }}
                            exitStyle={{
                                opacity: 0,
                                y: 20,
                            }}
                        >
                            {/* Institution Logo/Icon - Fixed 48px */}
                            {showSuccessState ? (
                                <View
                                    width={48}
                                    height={48}
                                    borderRadius={12}
                                    backgroundColor="$green900"
                                    alignItems="center"
                                    justifyContent="center"
                                    marginRight={12}
                                >
                                    <FruitfulLogoIcon width={32} height={32} />
                                </View>
                            ) : holisticAccounts[currentInstitutionIndex]
                                  ?.logo != null ? (
                                <View
                                    width={48}
                                    height={48}
                                    borderRadius={12}
                                    overflow="hidden"
                                    backgroundColor="$white"
                                    alignItems="center"
                                    justifyContent="center"
                                    marginRight={12}
                                >
                                    <Image
                                        source={{
                                            uri: holisticAccounts[
                                                currentInstitutionIndex
                                            ]?.logo,
                                        }}
                                        width={40}
                                        height={40}
                                        resizeMode="contain"
                                    />
                                </View>
                            ) : (
                                <View
                                    width={48}
                                    height={48}
                                    borderRadius={12}
                                    backgroundColor="$green900"
                                    alignItems="center"
                                    justifyContent="center"
                                    marginRight={12}
                                >
                                    <FruitfulLogoIcon width={32} height={32} />
                                </View>
                            )}

                            {/* Institution Name - Fills remaining space */}
                            <BodyMediumRegular
                                color="$black"
                                flexShrink={1}
                                flexGrow={1}
                            >
                                {showSuccessState
                                    ? 'Finished calculating bills'
                                    : holisticAccounts[currentInstitutionIndex]
                                            ?.institutionName != null
                                      ? `Scanning ${holisticAccounts[currentInstitutionIndex]?.institutionName ?? ''}`
                                      : 'Scanning all accounts for recurring bills'}
                            </BodyMediumRegular>

                            {/* Status Indicator - Fixed ~24px */}
                            {showSuccessState ? (
                                <View
                                    width={24}
                                    height={24}
                                    borderRadius={12}
                                    backgroundColor="$green300"
                                    alignItems="center"
                                    justifyContent="center"
                                    marginLeft={16}
                                >
                                    <Icon
                                        icon={CheckIcon}
                                        size={14}
                                        color="$green1100"
                                    />
                                </View>
                            ) : (
                                <View width={24} marginLeft={16}>
                                    <Spinner />
                                </View>
                            )}
                        </XStack>
                    </YStack>
                </YStack>
            </YStack>
        </SafeAreaView>
    );
};

export default SpinwheelBillsProcessing;
